import { client } from './api';

/**
 * Faxing an order to the physician who has to sign it.
 *
 * Separate from Mark as Sent Externally, which stays: that is the honest
 * record for mail, courier, email, and anything sent from somebody's own
 * machine. This is the one that actually puts the order on a fax line, with a
 * mark on every page so the signed copy can find its way back.
 */

/**
 * What happened to one order.
 *
 * `unresolved` is neither a success nor a failure, and the distinction is the
 * point of it: the fax line could not say whether the fax went, so the order
 * has not moved and nobody may send it again without looking first.
 */
export type OrderFaxOutcome = 'sent' | 'unresolved';

export interface OrderFaxResult {
  outcome: OrderFaxOutcome;
  message: string;
  send_id: string | null;
}

export async function sendOrderFax(orderId: string): Promise<OrderFaxResult> {
  const response = await client.post<OrderFaxResult, unknown, true>({
    responseType: 'json',
    // Without this the generated client resolves on a 4xx instead of
    // rejecting, and a refusal would show as a success.
    throwOnError: true,
    url: `/orders/${orderId}/fax`,
  });
  return response.data;
}

/** Where the order ended up once somebody answered for the returned page. */
export interface OrderFaxSignatureResult {
  order_id: string;
  order_status: string;
}

/**
 * Say whether the page that came back is actually signed.
 *
 * The only thing that moves an order on the strength of a returned fax. The
 * mark that matched the page identifies the order and concludes nothing about
 * the signature, so this is a person answering, recorded under their name.
 */
export async function decideOrderFaxSignature(
  orderId: string,
  signed: boolean,
  note: string | null = null
): Promise<OrderFaxSignatureResult> {
  const response = await client.post<OrderFaxSignatureResult, unknown, true>({
    responseType: 'json',
    // Same reason as the send above: without it a refusal resolves and would
    // be reported as success.
    throwOnError: true,
    url: `/orders/${orderId}/fax-signature`,
    body: { signed, note },
  });
  return response.data;
}

/** How many pages the returned fax has, for the in-place reviewer. */
export async function getReturnedFaxPageCount(
  orderId: string
): Promise<number> {
  const response = await client.get<
    { 200: { page_count: number } },
    unknown,
    true
  >({
    responseType: 'json',
    throwOnError: true,
    url: `/orders/${orderId}/returned-fax`,
  });
  return response.data.page_count;
}

/**
 * One page of the returned fax, as a blob URL the caller must revoke.
 *
 * Fetched through the authenticated client rather than pointed at by an image
 * tag, so it travels the same auth path as every other request instead of
 * resting on how cookies are treated for a subresource across two origins.
 *
 * It is an image rather than the fax itself on purpose: the page came from a
 * sender nobody vetted, and the inbox only ever serves these as downloads so
 * that whatever arrived cannot run in a reader's browser.
 */
export async function fetchReturnedFaxPage(
  orderId: string,
  index: number
): Promise<string> {
  const response = await client.get<Blob, unknown, true>({
    responseType: 'blob',
    throwOnError: true,
    url: `/orders/${orderId}/returned-fax/pages/${index}`,
  });
  return URL.createObjectURL(response.data);
}
